const normalize = (value = '') => String(value || '').replace(/\s+/g, ' ').trim();

const CARE_KNOWLEDGE_POLICY_VERSION = 'care_knowledge_promotion_v3';

export const AUTOMATIC_DURABLE_FACT_TYPES = Object.freeze([
  'communication_method',
  'safety_requirement',
]);

export const REVIEW_REQUIRED_OBSERVATION_TYPES = Object.freeze([
  'behavior',
  'trigger',
  'preference',
  'routine',
  'strategy',
  'progress',
  'recommendation',
  'goal',
  'one_time_outcome',
]);

const REVIEW_REQUIRED_KNOWLEDGE_CATEGORIES = new Set([
  'medication',
  'allergy',
  'goal',
]);

const REVIEWABLE_ORIGINS = new Set([
  'ai_extracted',
  'imported_document',
]);

const CAREGIVER_TYPED_ORIGINS = new Set([
  'caregiver_typed',
  'caregiver_marked_own',
]);

const DOCUMENT_SOURCE_TYPES = new Set([
  'file_import',
  'screenshot',
  'ocr_text',
  'doctor_note',
  'profile_document',
  'care_knowledge_document',
  'care_knowledge_photo',
  'care_knowledge_screenshot',
]);

const TYPE_CATEGORY_MAP = Object.freeze({
  medication: 'medication',
  medicines: 'medication',
  medicine: 'medication',
  meds: 'medication',
  pill: 'medication',
  pills: 'medication',
  allergy: 'allergy',
  allergies: 'allergy',
  allergic: 'allergy',
  goal: 'goal',
  goals: 'goal',
  appointment: 'appointment',
  appointments: 'appointment',
  visit: 'appointment',
  follow_up: 'appointment',
  followup: 'appointment',
  strategy: 'strategy',
  accommodation: 'strategy',
  accommodations: 'strategy',
  support: 'support',
  supports: 'support',
  communication: 'support',
  contact: 'support',
  contacts: 'support',
  profile: 'health_care',
  condition: 'health_care',
  conditions: 'health_care',
  baseline: 'health_care',
  routine: 'health_care',
  sleep: 'health_care',
  feeding: 'health_care',
  safety: 'health_care',
  risk: 'health_care',
  trigger: 'health_care',
  instruction: 'health_care',
  note: 'other',
  other: 'other',
});

const CHAPTER_CATEGORY_MAP = Object.freeze({
  what_helps: 'strategy',
  communication: 'support',
  watch_for: 'health_care',
  daily_life: 'health_care',
  safety: 'health_care',
  health_care: 'health_care',
  goals: 'goal',
  goal: 'goal',
});

const ONE_TIME_PATTERN = /\b(?:today|yesterday|this morning|this afternoon|this evening|last night|tonight|once|one time|this week|during this visit|at this appointment)\b/i;
const RECOMMENDATION_PATTERN = /\b(?:recommend(?:ed|ation)?|follow[- ]?up|goal|try|consider|monitor|schedule|appointment|referral|progress)\b/i;
const UNCERTAIN_FACT_PATTERN = /\b(?:possible|possibly|suspected|may have|might have|could have|rule out|unconfirmed)\b/i;
const DIAGNOSIS_PATTERN = /\b(?:diagnos(?:is|ed|es)|has (?:autism|adhd|asthma|diabetes|epilepsy|cerebral palsy)|medical condition)\b/i;
const ALLERGY_PATTERN = /\b(?:allerg(?:y|ies|ic)|anaphylaxis|anaphylactic)\b/i;
const ALLERGY_LIKE_PATTERN = /\b(?:can(?:not|'?t) have|can't have|cannot have|reaction|reacted|flagged(?: it)?|flagged by (?:a |the )?(?:nurse|doctor|provider)|nurse flagged|doctor flagged)\b/i;
const CONFIRMED_MEDICATION_PATTERN = /\b(?:takes?|prescribed|medication(?: is|:)|dose(?: is|:)|mg\b|mcg\b|tablet|capsule|confirmed (?:medication|dose|change)|changed (?:to|from)|discontinued by|started by)\b/i;
const STARTED_MEDICATION_PATTERN = /\b(?:start(?:ed|ing)?|initiated|begin(?:n?ed|ning)?|resumed|continue(?:d|ing)?)\b.{0,80}\b(?:\d+\s?(?:mg|mcg)|mg\b|mcg\b|tablet|capsule|dose\b)\b/i;
const COMMUNICATION_METHOD_PATTERN = /\b(?:uses? (?:an? )?(?:aac|communication device|speech device|sign language|gestures?|picture(?:s| board)?|pecs)|communicates? (?:using|with|through|by)|nonverbal|speaks? using)\b/i;
const EMERGENCY_CONTACT_PATTERN = /\b(?:emergency contact|call in an emergency|emergency phone|emergency number)\b/i;
const STABLE_PROFILE_PATTERN = /\b(?:date of birth|dob|legal name|preferred name|pronouns?|primary language|lives? with|caregiver is|guardian is)\b/i;
const SAFETY_REQUIREMENT_PATTERN = /\b(?:must|requires?|needs?)\b.{0,80}\b(?:supervision|epipen|epi-pen|rescue medication|helmet|seat belt|life jacket|locked|emergency plan|two-person assist|1:1|one-to-one)\b/i;

const getText = (item = {}) => normalize([
  item.title,
  item.content || item.summary,
  item.sourceText,
].filter(Boolean).join(' '));

const getPrimarySourceRef = (item = {}) => (
  Array.isArray(item.sourceRefs) ? item.sourceRefs[0] || {} : {}
);

export const normalizePolicyCategory = (item = {}) => {
  const categoryToken = normalize(
    item.knowledgeType
    || item.factType
    || item.bucket
    || item.profileChapterId
    || item.profileField
    || ''
  ).toLowerCase().replace(/[\s-]+/g, '_');
  const chapterToken = normalize(item.profileChapterId).toLowerCase().replace(/[\s-]+/g, '_');
  const text = getText(item);

  if (text) {
    if (ALLERGY_PATTERN.test(text) || ALLERGY_LIKE_PATTERN.test(text)) {
      return 'allergy';
    }
    if (CONFIRMED_MEDICATION_PATTERN.test(text) || STARTED_MEDICATION_PATTERN.test(text)) {
      return 'medication';
    }
    if (RECOMMENDATION_PATTERN.test(text)) {
      return 'goal';
    }
    if (/\b(?:appointment|appointments|follow[- ]?up|visit|schedule|scheduled|rescheduled)\b/i.test(text)) {
      return 'appointment';
    }
    if (COMMUNICATION_METHOD_PATTERN.test(text)) {
      return 'support';
    }
    if (
      DIAGNOSIS_PATTERN.test(text)
      || EMERGENCY_CONTACT_PATTERN.test(text)
      || STABLE_PROFILE_PATTERN.test(text)
      || SAFETY_REQUIREMENT_PATTERN.test(text)
      || /\b(?:condition|symptom|medical|health|therapy|therapist|care plan|crisis plan|baseline|sleep|feeding|routine|trigger|risk|safety)\b/i.test(text)
    ) {
      return 'health_care';
    }
    if (/\b(?:strategy|accommodation|helps?|helpful|works? well|works? best|calm|transition|visual|countdown|prep|prompt|remind|support)\b/i.test(text)) {
      return /\b(?:support|help|helpful|assistance|communication|aac|gesture|sign language|device)\b/i.test(text)
        ? 'support'
        : 'strategy';
    }
  }

  const mappedCategory = TYPE_CATEGORY_MAP[categoryToken]
    || CHAPTER_CATEGORY_MAP[chapterToken];
  if (mappedCategory) {
    return mappedCategory;
  }

  return 'other';
};

export const getSourceOrigin = (item = {}) => {
  const provenanceOrigin = normalize(item.sourceProvenance?.origin).toLowerCase();
  if (CAREGIVER_TYPED_ORIGINS.has(provenanceOrigin) || provenanceOrigin === 'ai_extracted' || provenanceOrigin === 'imported_document') {
    return provenanceOrigin;
  }

  if (normalize(item.capturedBy?.authorship).toLowerCase() === 'caregiver') {
    return 'caregiver_typed';
  }

  const sourceRef = getPrimarySourceRef(item);
  const sourceType = normalize(
    item.sourceType
    || sourceRef.sourceType
    || item.sourceRefs?.[0]?.sourceType
    || ''
  ).toLowerCase();
  const sourceContext = normalize(
    item.sourceContext
    || sourceRef.sourceContext
    || item.sourceRefs?.[0]?.sourceContext
    || ''
  ).toLowerCase();

  if (
    DOCUMENT_SOURCE_TYPES.has(sourceType)
    || sourceContext === 'imported_document'
    || sourceContext === 'document'
  ) {
    return 'imported_document';
  }

  return 'ai_extracted';
};

const classifyAutomaticDurableFact = (item = {}) => {
  const type = normalize(item.knowledgeType || item.factType).toLowerCase();
  const text = getText(item);
  if (
    !text
    || ONE_TIME_PATTERN.test(text)
    || RECOMMENDATION_PATTERN.test(text)
    || UNCERTAIN_FACT_PATTERN.test(text)
  ) return '';
  if (
    ALLERGY_PATTERN.test(text)
    || ALLERGY_LIKE_PATTERN.test(text)
    || (type === 'condition' && DIAGNOSIS_PATTERN.test(text))
    || (type === 'medication' && CONFIRMED_MEDICATION_PATTERN.test(text))
    || (type === 'contact' && EMERGENCY_CONTACT_PATTERN.test(text))
    || (type === 'profile' && STABLE_PROFILE_PATTERN.test(text))
  ) return '';
  if (COMMUNICATION_METHOD_PATTERN.test(text)) return 'communication_method';
  if (type === 'safety' && SAFETY_REQUIREMENT_PATTERN.test(text)) return 'safety_requirement';
  return '';
};

const requiresCareKnowledgeReview = (item = {}) => (
  getCareKnowledgePromotionDecision(item).needsCaregiverDecision
);

const getCareKnowledgePromotionDecision = (
  item = {},
  { hasExistingMatch = false } = {}
) => {
  const category = normalizePolicyCategory(item);
  const origin = getSourceOrigin(item);
  const profilePlacement = category;
  const durability = normalize(item.durability).toLowerCase().replace(/[\s-]+/g, '_');

  if (hasExistingMatch) {
    return {
      action: 'reinforce_existing',
      reason: 'matches_existing_knowledge',
      category,
      origin,
      profilePlacement,
      needsCaregiverDecision: false,
      policyVersion: CARE_KNOWLEDGE_POLICY_VERSION,
    };
  }

  if (
    (REVIEWABLE_ORIGINS.has(origin) || CAREGIVER_TYPED_ORIGINS.has(origin))
    && REVIEW_REQUIRED_KNOWLEDGE_CATEGORIES.has(category)
  ) {
    return {
      action: 'review_required',
      reason: 'category_requires_caregiver_decision',
      category,
      origin,
      profilePlacement,
      needsCaregiverDecision: true,
      policyVersion: CARE_KNOWLEDGE_POLICY_VERSION,
    };
  }

  if (origin === 'caregiver_typed' || origin === 'caregiver_marked_own') {
    return {
      action: 'auto_promote',
      reason: 'caregiver_typed_auto_save',
      category,
      origin,
      profilePlacement,
      needsCaregiverDecision: false,
      policyVersion: CARE_KNOWLEDGE_POLICY_VERSION,
    };
  }

  if (REVIEWABLE_ORIGINS.has(origin) && durability === 'one_time_observation') {
    return {
      action: 'observe_pattern',
      reason: 'one_time_observation_requires_independent_support',
      category,
      origin,
      profilePlacement,
      needsCaregiverDecision: false,
      policyVersion: CARE_KNOWLEDGE_POLICY_VERSION,
    };
  }

  if (REVIEWABLE_ORIGINS.has(origin) && durability !== 'explicit_durable_fact') {
    return {
      action: 'review_required',
      reason: 'durability_not_established',
      category,
      origin,
      profilePlacement,
      needsCaregiverDecision: true,
      policyVersion: CARE_KNOWLEDGE_POLICY_VERSION,
    };
  }

  return {
    action: 'auto_promote',
    reason: 'category_auto_promotes',
    category,
    origin,
    profilePlacement,
    needsCaregiverDecision: false,
    policyVersion: CARE_KNOWLEDGE_POLICY_VERSION,
  };
};

const isAutomaticallyPersistableCareKnowledge = (item = {}) => (
  getCareKnowledgePromotionDecision(item).action === 'auto_promote'
);

const isTemporaryCareActionSuggestion = (item = {}) => (
  RECOMMENDATION_PATTERN.test(getText(item)) || ONE_TIME_PATTERN.test(getText(item))
);

const isMeaningfulKnowledgeTrigger = (item = {}) => (
  normalize(item.knowledgeType).toLowerCase() !== 'trigger'
  || getText(item).length > 0
);

export {
  classifyAutomaticDurableFact,
  getCareKnowledgePromotionDecision,
  requiresCareKnowledgeReview,
  isAutomaticallyPersistableCareKnowledge,
  isTemporaryCareActionSuggestion,
  isMeaningfulKnowledgeTrigger,
};
